"use client";

import { useState, useRef, useEffect, type FormEvent, type KeyboardEvent } from "react";
import { ShieldCheck } from "lucide-react";
import { useCountdown } from "@/hooks/use-countdown";

interface OtpStepProps {
  phone: string;
  onSubmit: (otp: string) => void;
  onResend: () => void;
  onBack: () => void;
  isPending: boolean;
  error: Error | null;
}

const OTP_LENGTH = 6;

export function OtpStep({
  phone,
  onSubmit,
  onResend,
  onBack,
  isPending,
  error,
}: OtpStepProps) {
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { seconds, isActive, start } = useCountdown(30);

  useEffect(() => {
    start();
    inputRefs.current[0]?.focus();
  }, [start]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const next = [...digits];
    next[index] = value.slice(-1);
    setDigits(next);

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;

    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setDigits(next);

    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const otp = digits.join("");
    if (otp.length === OTP_LENGTH) {
      onSubmit(otp);
    }
  };

  const handleResend = () => {
    setDigits(Array(OTP_LENGTH).fill(""));
    inputRefs.current[0]?.focus();
    onResend();
    start();
  };

  const otpComplete = digits.every((d) => d !== "");

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <ShieldCheck className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-headline">
          Vérification du numéro
        </h2>
        <p className="mt-1 text-sm text-body">
          Pour votre sécurité, nous voulons confirmer que c&apos;est bien vous.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-headline mb-2">
            Entrez le code à 6 chiffres
          </label>
          <div className="flex justify-center gap-2" onPaste={handlePaste}>
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputRefs.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="h-12 w-10 rounded-lg border border-gray-200 dark:border-[#313131] dark:bg-[#313131] dark:text-gray-400 text-center text-lg font-semibold focus:outline-none hover:border-black focus:border-black dark:focus:border-white dark:hover:border-gray-400 dark:hover:border-gray-500 dark:focus:border-gray-400"
              />
            ))}
          </div>
        </div>

        <p className="text-center text-sm text-body">
          Nous avons envoyé un code au {phone}
        </p>

        <div className="flex items-center justify-center gap-2 text-sm">
          <button
            type="button"
            onClick={handleResend}
            disabled={isActive}
            className={`font-medium ${
              isActive
                ? "text-gray-400 dark:text-gray-500 dark:text-gray-400 dark:text-gray-500 cursor-not-allowed"
                : "text-primary hover:underline"
            }`}
          >
            Renvoyer le code{isActive ? ` (${seconds})` : ""}
          </button>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
            {error.message}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 rounded-full border border-gray-300 dark:border-[#313131] py-3 text-sm font-bold text-headline transition hover:bg-gray-50 dark:bg-[#313131] dark:hover:bg-[#313131]"
          >
            Retour
          </button>
          <button
            type="submit"
            disabled={!otpComplete || isPending}
            className="flex-1 rounded-full bg-secondary py-3 text-sm font-bold text-white transition hover:bg-primary/90 hover:text-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Vérification..." : "Valider"}
          </button>
        </div>
      </form>
    </div>
  );
}
